import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { C, FONT, R, SP } from '../theme/tokens';
import {
  CALC_REGISTRY,
  CATEGORY_META,
  CATEGORY_ORDER,
  calcsByCategory,
} from '../utils/calcRegistry';
import { loadUsedCalcs, UsedCalcs } from '../utils/usedCalcs';

type Nav = {
  navigate: (screen: string, params?: any) => void;
  addListener: (event: string, cb: () => void) => () => void;
};

export default function ProgressScreen({ navigation }: { navigation: Nav }) {
  const [used, setUsed] = useState<UsedCalcs>({ routes: {} });

  const refresh = useCallback(async () => {
    setUsed(await loadUsedCalcs());
  }, []);

  useEffect(() => {
    const unsub = navigation.addListener('focus', refresh);
    return unsub;
  }, [navigation, refresh]);

  const totalCount = CALC_REGISTRY.length;
  const usedCount = useMemo(
    () => CALC_REGISTRY.filter((c) => used.routes[c.route]).length,
    [used],
  );
  const pct = totalCount ? Math.round((usedCount / totalCount) * 100) : 0;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.inner}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.eyebrowRow}>
            <View style={styles.eyebrowBar} />
            <Text style={styles.eyebrowText}>COVERAGE REPORT</Text>
          </View>
          <Text style={styles.title}>Calculators used</Text>
          <Text style={styles.subtitle}>
            Which calculators you've opened at least once. Use this as a
            coverage report for field work or study.
          </Text>
        </View>

        {/* Summary */}
        <View style={styles.summaryCard}>
          <Text style={styles.summaryBig}>
            {usedCount}
            <Text style={styles.summarySmall}> / {totalCount}</Text>
          </Text>
          <Text style={styles.summaryLabel}>Calculators used</Text>
          <View style={styles.barTrack}>
            <View style={[styles.barFill, { width: `${pct}%` }]} />
          </View>
          <Text style={styles.pctText}>{pct}%</Text>
        </View>

        {/* Category breakdowns */}
        {CATEGORY_ORDER.map((cat) => {
          const items = calcsByCategory(cat);
          const usedItems = items.filter((c) => used.routes[c.route]);
          const catPct = items.length
            ? Math.round((usedItems.length / items.length) * 100)
            : 0;
          const meta = CATEGORY_META[cat];

          return (
            <View key={cat} style={styles.categoryBlock}>
              <View style={styles.categoryHeader}>
                <Ionicons name={meta.icon as any} size={16} color={C.ink2} />
                <View style={{ flex: 1 }}>
                  <Text style={styles.categoryTitle}>{meta.label}</Text>
                  <Text style={styles.categoryCount}>
                    {usedItems.length} / {items.length} · {catPct}%
                  </Text>
                </View>
              </View>
              <View style={styles.catBarTrack}>
                <View style={[styles.catBarFill, { width: `${catPct}%` }]} />
              </View>

              <View style={styles.chips}>
                {items.map((c) => {
                  const u = !!used.routes[c.route];
                  return (
                    <Pressable
                      key={c.route}
                      style={({ pressed }) => [
                        styles.chip,
                        u ? styles.chipUsed : styles.chipUnused,
                        pressed && styles.pressed,
                      ]}
                      onPress={() =>
                        navigation.navigate(c.category, { screen: c.route })
                      }
                    >
                      {u ? (
                        <Ionicons
                          name="checkmark"
                          size={12}
                          color={C.accent}
                          style={{ marginRight: 4 }}
                        />
                      ) : null}
                      <Text style={[styles.chipText, u ? styles.chipTextUsed : null]}>
                        {c.title}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg },
  content: { padding: SP.md, paddingBottom: SP.xl },
  inner: { width: '100%', maxWidth: 980, alignSelf: 'center' },

  header: { marginBottom: SP.md },
  eyebrowRow: { flexDirection: 'row', alignItems: 'center', marginBottom: SP.xs },
  eyebrowBar: {
    width: 4,
    height: 11,
    backgroundColor: C.accent,
    borderRadius: 1,
    marginRight: SP.sm,
  },
  eyebrowText: {
    fontFamily: FONT.mono,
    fontSize: 11,
    color: C.dim,
    letterSpacing: 0.8,
  },
  title: {
    fontFamily: FONT.sansBold,
    fontSize: 26,
    color: C.ink,
    letterSpacing: -0.3,
  },
  subtitle: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
    marginTop: SP.xs + 2,
    lineHeight: 19,
  },

  summaryCard: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    padding: SP.md,
    marginBottom: SP.md,
    alignItems: 'center',
  },
  summaryBig: { fontFamily: FONT.monoBold, fontSize: 48, color: C.ink, lineHeight: 54 },
  summarySmall: { fontFamily: FONT.mono, fontSize: 22, color: C.ink2 },
  summaryLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: 2,
    marginBottom: SP.md,
  },
  barTrack: {
    width: '100%',
    height: 6,
    backgroundColor: C.panel2,
    borderRadius: 3,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: C.edge,
  },
  barFill: { height: '100%', backgroundColor: C.accent },
  pctText: {
    fontFamily: FONT.mono,
    fontSize: 12,
    color: C.ink2,
    marginTop: SP.sm,
  },

  categoryBlock: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    padding: SP.md,
    marginBottom: SP.md,
  },
  categoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SP.sm + 2,
    marginBottom: SP.sm,
  },
  categoryTitle: { fontFamily: FONT.sansMed, fontSize: 14, color: C.ink },
  categoryCount: { fontFamily: FONT.mono, fontSize: 11, color: C.dim, marginTop: 1 },
  catBarTrack: {
    height: 4,
    backgroundColor: C.panel2,
    borderRadius: 2,
    overflow: 'hidden',
    marginBottom: SP.sm + 2,
  },
  catBarFill: { height: '100%', backgroundColor: C.accent },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: SP.xs + 2 },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SP.sm + 2,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
  },
  chipUsed: { backgroundColor: C.panel2, borderColor: C.edge },
  chipUnused: { backgroundColor: 'transparent', borderColor: C.edge },
  chipText: { fontFamily: FONT.sans, fontSize: 12, color: C.dim },
  chipTextUsed: { color: C.ink },
  pressed: { opacity: 0.85 },
});
